/* eslint-disable no-nested-ternary */
import { useDevices, useVideoContext } from '@brands/hooks';
import { SELECTED_VIDEO_INPUT_KEY } from '@brands/hooks/video-hooks/useLocalTracks';
import CheckIcon from '@mui/icons-material/Check';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TbVideo, TbVideoOff } from 'react-icons/tb';

import { useLocalVideoToggle } from '../../../hooks';
import styles from './styles.module.scss';

interface ToggleVideoButtonProps {
  disabled?: boolean;
  className?: string;
  onAfterSelectDevice?: (deviceId: string) => Promise<void> | void;
  layout?: 'mini' | 'full';
}

const ToggleVideoButton: FC<ToggleVideoButtonProps> = ({ disabled, className, onAfterSelectDevice, layout }) => {
  const [isVideoEnabled, toggleVideoEnabled] = useLocalVideoToggle();
  const lastClickTimeRef = useRef(0);

  const { videoInputDevices } = useDevices();

  const { replaceTrack } = useVideoContext();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const [selectedCamId, setSelectedCamId] = useState<string>('');

  useEffect(() => {
    if (!videoInputDevices || videoInputDevices.length === 0) return;

    const stored = window.localStorage.getItem(SELECTED_VIDEO_INPUT_KEY);
    const initial = stored || videoInputDevices[0].deviceId;

    setSelectedCamId(initial);

    if (stored && !videoInputDevices.some((d) => d.deviceId === stored)) {
      window.localStorage.setItem(SELECTED_VIDEO_INPUT_KEY, videoInputDevices[0].deviceId);
      setSelectedCamId(videoInputDevices[0].deviceId);
    }
  }, [videoInputDevices]);

  const toggleVideo = useCallback(() => {
    if (disabled) return;
    if (Date.now() - lastClickTimeRef.current > 500) {
      lastClickTimeRef.current = Date.now();
      toggleVideoEnabled();
    }
  }, [toggleVideoEnabled, disabled]);

  const selectedLabel = useMemo(() => {
    const found = videoInputDevices?.find((d) => d.deviceId === selectedCamId);
    return found?.label || 'System Default';
  }, [videoInputDevices, selectedCamId]);

  const handleSelect = useCallback(
    async (deviceId: string) => {
      setSelectedCamId(deviceId);
      setAnchorEl(null);

      window.localStorage.setItem(SELECTED_VIDEO_INPUT_KEY, deviceId);

      replaceTrack(deviceId);

      if (onAfterSelectDevice) await onAfterSelectDevice(deviceId);
    },
    [replaceTrack, onAfterSelectDevice],
  );

  return (
    <div
      className={`fs-unmask ${className || ''}`}
      style={{
        height: layout === 'mini' ? 30 : 42,
        borderRadius: 999,
        background: '#E0E0E0',
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
      }}
    >
      <Tooltip title={selectedLabel} placement="top">
        <IconButton
          disabled={disabled}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          size="large"
          style={{
            width: layout === 'mini' ? 30 : 42,
            height: layout === 'mini' ? 30 : 42,
            borderRadius: 0,
            color: '#3D3D3D',
          }}
          data-cy-video-device-dropdown
        >
          {menuOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Tooltip>

      <button
        type="button"
        disabled={disabled}
        onClick={toggleVideo}
        data-cy-video-toggle
        style={{
          width: layout === 'mini' ? 30 : 42,
          height: layout === 'mini' ? 30 : 42,
          border: 'none',
          background: 'transparent',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'grid',
          placeItems: 'center',
          padding: 0,
        }}
      >
        <span
          className={styles.vcVideoIcon}
          style={{
            width: '-webkit-fill-available',
            height: '-webkit-fill-available',
            borderRadius: 999,
            background: '#F2F2F2',
            display: 'grid',
            placeItems: 'center',
            color: '#2C2C2C',
          }}
        >
          {isVideoEnabled ? <TbVideo size={20} /> : <TbVideoOff size={20} />}
        </span>
      </button>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={() => setAnchorEl(null)}
        PaperProps={{ style: { width: 320, borderRadius: 12 } }}
        sx={{ zIndex: 2000 }}
      >
        <MenuItem disabled style={{ opacity: 0.9 }}>
          <strong>Select a camera</strong>
        </MenuItem>

        {!videoInputDevices || videoInputDevices.length === 0 ? (
          <MenuItem disabled>
            <span style={{ fontSize: 13, color: '#666' }}>No camera devices found</span>
          </MenuItem>
        ) : (
          videoInputDevices.map((d, idx) => (
            <MenuItem key={d.deviceId} onClick={() => handleSelect(d.deviceId)} style={{ display: 'flex', gap: 8 }}>
              {d.deviceId === selectedCamId ? <CheckIcon fontSize="small" /> : <span style={{ width: 24 }} />}
              <span style={{ fontSize: 14 }}>{d.label || `Camera ${idx + 1}`}</span>
            </MenuItem>
          ))
        )}
      </Menu>
    </div>
  );
};

ToggleVideoButton.defaultProps = {
  disabled: false,
  className: '',
  onAfterSelectDevice: undefined,
};

export default ToggleVideoButton;
